import { useCallback, useMemo, useState } from "react";
import { Button, Card, Container, Form, FormControl, FormGroup, FormLabel } from "react-bootstrap";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";
import { arrayMove } from "react-sortable-hoc";
import { withAuthorizationVerifier } from "../../../components/authorization-verifier";
import ContentWrapper from "../../../components/content-wrapper";
import { FullWidthLoader } from "../../../components/full-width-loader";
import { useLocaleProvider } from "../../../components/locale-provider";
import { useLoyaltyBrand } from "../../../components/loyalty-brand-provider";
import { usePropertyLocales } from "../../../components/property-locale-provider";
import { useProperty } from "../../../components/property-provider";
import { SectionHeading } from "../../../components/section-heading";
import { tickerHookFormValidation } from "../../shared/crud/form-validation";
import { SortableSelect } from "../../shared/widgets/SortableSelect";
import { TranslationComponent, TranslationForm } from "../../shared/widgets/TranslationComponent";
import { ErrorField } from "../../shared/widgets/TranslationComponent/styles";
import { LoyaltyTag } from "../domain/LoyaltyTag";
import { LoyaltyTagSection, LoyaltyTagSectionType } from "../domain/LoyaltyTagSection";
import { ModalActionType, useModal } from "../infrastructure/hooks/modal.hook";
import { useSection, useSections } from "../infrastructure/hooks/sections.hook";
import { useTags } from "../infrastructure/hooks/tags.hook";
import { loyaltyTagSectionEditMessages } from "./LoyaltyTagSectionEdit.messages";

type PreferenceDto = { label: string; value: string; tag: LoyaltyTag };
type PreferenceSectionDto = { ticker: string; preferences?: PreferenceDto[] } & TranslationForm;

function toPreferenceSectionDto(
  section: LoyaltyTagSection,
  allowedLocales: string[],
  locale: string
): PreferenceSectionDto {
  return {
    ticker: section.ticker,
    preferences: section.tags.map(tag => ({
      label: tag.getTranslation(locale).name,
      value: tag.ticker,
      tag: tag,
    })),
    translationsList: allowedLocales.map(locale => {
      const translation = section.translations.find(t => t.locale === locale);
      return {
        locale: locale,
        name: translation?.name ?? "",
        description: translation?.description ?? "",
      };
    }),
  };
}

function ILoyaltyTagSectionEdit() {
  const { id } = useParams<{ id?: string }>();
  const brandId = useLoyaltyBrand()?.externalSystemId || "";
  const { currentLocale } = useLocaleProvider();
  const allowedLocales = usePropertyLocales();
  const property = useProperty();
  const history = useHistory();
  const intl = useIntl();
  const { showModal } = useModal();

  const [loading, setLoading] = useState(false);

  const { saveSection, updateSection } = useSections(brandId);
  const { section, isLoading: isSectionLoading } = useSection(brandId, id);
  const { tags, isLoading: isTagsLoading } = useTags(brandId, { loadTags: true, globals: true });

  const form = useForm<PreferenceSectionDto>({
    values: section ? toPreferenceSectionDto(section, allowedLocales, currentLocale) : undefined,
  });

  const preferenceOptions = useMemo(
    () =>
      tags?.map(tag => ({
        label: tag.getTranslation(currentLocale).name,
        value: tag.ticker,
        tag: tag,
      })),
    [currentLocale, tags]
  );

  const handleGoBack = useCallback(() => history.push(`/${property.ticker}/loyalty-preferences`), [
    history,
    property.ticker,
  ]);

  const handleSubmit = useCallback(
    async (data: PreferenceSectionDto) => {
      try {
        setLoading(true);
        if (id && section) {
          const updatedSection = LoyaltyTagSection.of({
            ...section,
            ticker: data.ticker,
            translations: data.translationsList,
            tags: data.preferences?.map(p => p.tag) ?? [],
          });

          await updateSection(updatedSection);
          showModal({ type: ModalActionType.SUCCESS_UPDATE }, handleGoBack);
        } else {
          const newSection = LoyaltyTagSection.create({
            ticker: data.ticker,
            translations: data.translationsList,
            type: LoyaltyTagSectionType.PREFERENCE_SECTION,
            tags: data.preferences?.map(p => p.tag) ?? [],
          });

          await saveSection(newSection);
          showModal({ type: ModalActionType.SUCCESS_CREATE }, handleGoBack);
        }
      } catch (e) {
        setLoading(false);
        if (id) {
          console.error("Error updating loyalty preference section", e);
          showModal({ type: ModalActionType.ERROR_UPDATE }, () => {});
        } else {
          console.error("Error creating loyalty preference section", e);
          showModal({ type: ModalActionType.ERROR_CREATE }, () => {});
        }
      }
    },
    [handleGoBack, id, saveSection, section, showModal, updateSection]
  );

  if (!brandId || loading || isSectionLoading) {
    return <FullWidthLoader />;
  }

  return (
    <ContentWrapper>
      <SectionHeading>
        <FormattedMessage {...loyaltyTagSectionEditMessages.title} />
      </SectionHeading>
      <Container className="p-4" fluid={true}>
        <Card className="p-3">
          <FormProvider {...form}>
            <Form>
              <FormGroup>
                <FormLabel htmlFor="sectionTicker">
                  <FormattedMessage {...loyaltyTagSectionEditMessages.ticker} /> *
                </FormLabel>
                <FormControl
                  id="sectionTicker"
                  type="text"
                  {...form.register("ticker", tickerHookFormValidation(intl))}
                />

                {form.formState.errors["ticker"] && (
                  <ErrorField>{form.formState.errors["ticker"].message}</ErrorField>
                )}
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="sectionPreferences">
                  <FormattedMessage {...loyaltyTagSectionEditMessages.preferences} />
                </FormLabel>
                <Controller
                  name="preferences"
                  control={form.control}
                  render={({ field }) => (
                    <SortableSelect
                      {...field}
                      id="sectionPreferences"
                      placeholder={intl.formatMessage(
                        loyaltyTagSectionEditMessages.preferencesPlaceholder
                      )}
                      options={preferenceOptions}
                      isLoading={isTagsLoading}
                      onSortEnd={({ oldIndex, newIndex }) => {
                        const preferences = form.getValues("preferences") ?? [];
                        const orderedPreferences = arrayMove(preferences, oldIndex, newIndex);
                        form.setValue("preferences", orderedPreferences);
                      }}
                    />
                  )}
                />
              </FormGroup>

              <FormGroup>
                <TranslationComponent allowedLocales={allowedLocales} />
              </FormGroup>

              <div className="flex gap-4">
                <Button variant="primary" type="submit" onClick={form.handleSubmit(handleSubmit)}>
                  <FormattedMessage {...loyaltyTagSectionEditMessages.save} />
                </Button>
                <Button variant="secondary" onClick={handleGoBack}>
                  <FormattedMessage {...loyaltyTagSectionEditMessages.back} />
                </Button>
              </div>
            </Form>
          </FormProvider>
        </Card>
      </Container>
    </ContentWrapper>
  );
}

export const LoyaltyTagSectionEdit = withAuthorizationVerifier(ILoyaltyTagSectionEdit);
